// this comment tells babel to convert jsx to calls to a function called jsx instead of React.createElement
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useEffect, Fragment } from 'react';
import { BaseTemplate, Card, PageHead } from '@myob/myob-widgets';
import LoadingWrapper from 'components/LoadingWrapper';
import PaymentDetail from './components/PaymentDetail';
import { useDispatch, useSelector } from 'react-redux';
import {
    EPaymentProfileActionType,
    IFetchPaymentProfileAction,
    IPaymentProfileItem,
} from 'modules/PaymentProfile/reducers/type';
import { RootState } from 'stores';
import { getBusinessId } from 'helpers/query';
import MultiplePaymentView from 'modules/PaymentProfile/components/MessageView/MultiplePayment';
import OnAccountPaymentView from 'modules/PaymentProfile/components/MessageView/OnAccountPayment';
import { Notification } from 'components/Notification';
import { digestPaymentProfileData } from 'helpers/paymentProfile';
import { PaymentProfileState } from 'modules/PaymentProfile/reducers';
import { ETelemetryNames } from 'telemetry/type';
import { LocationStateInUnauthorizedPage } from 'pages/ErrorPages/Unauthorized/Unauthorized';
import { useHistory } from 'react-router-dom';
import { ERoutePath } from 'router';
import { EPage } from 'pages/ErrorPages/Unauthorized/pageContent';
import Breadcrumbs from 'components/Breadcrumbs';
import { BreadcrumbsWrapper } from 'modules/MyBills/styles';
import { useTrackStatus } from 'modules/PaymentProfile/hooks/PaymentProfile';
import NavigationBar from 'components/NavigationBar';

const PaymentProfile: React.FC = () => {
    const businessId = getBusinessId();
    const dispatch = useDispatch();
    const history = useHistory<LocationStateInUnauthorizedPage>();
    const paymentProfile = useSelector<RootState, PaymentProfileState>(state => state.paymentProfile);
    const trackStatus = useTrackStatus();
    const { isLoading } = paymentProfile;
    const digest = digestPaymentProfileData(paymentProfile);

    useEffect(() => {
        dispatch<IFetchPaymentProfileAction>({
            type: EPaymentProfileActionType.FETCH_REQUESTED,
            payload: { params: { businessId } },
        });
    }, [businessId, dispatch]);

    if (isLoading) {
        return (
            <Fragment>
                <NavigationBar />
                <LoadingWrapper isLoading={isLoading} />
            </Fragment>
        );
    }

    if (digest.isError || digest.isPartnerPay || digest.missesActivePaymentProfile) {
        setTimeout(
            () =>
                history.push({
                    pathname: ERoutePath.Unauthorized,
                    state: {
                        pageName: EPage.PaymentProfile,
                    },
                }),
            0,
        );
        return <NavigationBar />;
    }

    const cardBody = () => {
        trackStatus(digest);
        if (digest.isMultiActivePay) {
            return <MultiplePaymentView />;
        }
        if (digest.isOnAccount) {
            return <OnAccountPaymentView />;
        }
        return <PaymentDetail paymentItem={digest.activePaymentProfile?.item as IPaymentProfileItem} />;
    };
    return (
        <Fragment>
            <NavigationBar />
            <BaseTemplate>
                <BreadcrumbsWrapper>
                    <Breadcrumbs
                        parentPageName="Billing and payments"
                        parentPathname="billingAndPayments"
                        currentPageName="Payment details"
                        telemetryEventName={ETelemetryNames.PaymentProfile}
                    />
                </BreadcrumbsWrapper>
                <Notification />
                <PageHead title="Payment details" />
                <Card id="paymentProfile" body={<Card.Body child={cardBody()} />} />
            </BaseTemplate>
        </Fragment>
    );
};

export default PaymentProfile;
